import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { capitalize } from '@ember/string'
import { createClient } from 'contentful'
import type { EntryCollection, EntrySkeletonType, ContentfulClientApi, Entry, CreateClientParams } from 'contentful'
import { hash } from 'rsvp'
import config from 'teacher-ui-client/config/environment'
import { getContentfulLocale } from 'teacher-ui-client/utils/locales'
import { contentVariant } from 'teacher-ui-client/utils/content-variant'
import {
  getContentfulLongDomainCode,
  getContentfulShortDomainCode,
} from 'teacher-ui-client/contentful-client/contentful-domains'
import type { PrintablesTabMeta } from 'teacher-ui-client/subscription-type/printables/tabs'
import {
  PRINTABLES_CATEGORY_READER,
  PRINTABLES_CATEGORY_GENERAL,
} from 'teacher-ui-client/subscription-type/printables/tabs'
import { isEmpty, isPresent } from '@ember/utils'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { Precinct } from 'teacher-ui-client/utils/precincts'
import type {
  SummerGuideSkeleton,
  LessonDataSkeleton,
  LessonPreviewSkeleton,
  LessonResourceSkeleton,
  PrecinctResourceSkeleton,
  PrintablesLibrarySetSkeleton,
  PrintablesQuickFiltersSkeleton,
  CaseStudySkeleton,
  ResearchReportSkeleton,
  TeacherGuideSkeleton,
  CurriculumMapSkeleton,
  CertificateSkeleton,
  MathseedsPrimeLessonSkeleton,
  TeacherResourceCategorySkeleton,
  BookNotesSkeleton,
  TeacherResourceFilesSkeleton,
  TeacherResourceActivitySkeleton,
  TeacherResourceCategoryEntry,
  LessonResourceType,
  PrecinctResourceType,
  ProductUnderscored,
  RolloverGuideSkeleton,
  QuickStartResourceSkeleton,
  HomeschoolMaxWebinarSkeleton,
} from 'teacher-ui-client/contentful-client/entry-types'
import { isTypeTeacherResourceCategory } from 'teacher-ui-client/contentful-client/entry-types'

import { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import type Session from 'teacher-ui-client/services/session'
import { groupBy, keyBy } from 'lodash'
import type { Log } from '@blakeelearning/log'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import type Features from 'teacher-ui-client/services/features'
import type { GradeId } from 'teacher-ui-client/services/mathseeds-prime'
import type Owner from '@ember/owner'
import { isAbortError, isUnauthorizedError } from 'teacher-ui-client/errors/http-error'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type WindowService from 'teacher-ui-client/services/window'

const CONTENTFUL_PROXY_ACCESS_TOKEN = 'contentful-proxy'

type Role = 'teacher' | 'subco'

interface RequestParamsRequired {
  content_type: string
}
interface RequestParamsOptional {
  locale: string
  limit: number
  include: number
  order: string[]
  [param: string]: string[] | string | number[] | number | boolean | undefined
}

type RequestParams = RequestParamsRequired & Partial<RequestParamsOptional>

interface LessonResourceFilter {
  phases?: number[]
}

interface LessonPreviewFilter {
  phases?: number[]
}

type LessonDataId = string

export type BookNote = Resolved<ReturnType<ContentfulClientService['getBookNotesForBookCode']>>[number]

export default class ContentfulClientService extends Service {
  @service session!: Session

  @service authToken!: AuthToken

  @service log!: Log

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service features!: Features

  @service window!: WindowService

  @tracked showEditLinks = false

  get language() {
    return this.session.currentTeacher.language
  }

  get countryCode() {
    return this.session.currentSchool.countryCode
  }

  get accent() {
    return contentVariant(this.session.currentSchool?.accent?.extension ?? 'au')
  }

  client: ContentfulClientApi<'WITHOUT_UNRESOLVABLE_LINKS'>

  // tracks the authToken value the current `client` was built with, so we can detect when it's gone stale.
  private clientToken?: string

  constructor(owner?: Owner) {
    super(owner)
    // create a client that will always remove unresolvable links from entries.
    this.clientToken = this.authToken.token
    this.client = createClient(this.clientConfig).withoutUnresolvableLinks
  }

  // add any other environments you want to select, to this list. They should be accessible via the develop tokens
  get availableEnvironments() {
    return ['master', 'develop']
  }

  private get environment() {
    if (config.contentful.showSelector) {
      const envFromStorage = window.localStorage.getItem('teacher-ui-client.contentful.selected-environment') ?? ''
      if (this.availableEnvironments.includes(envFromStorage)) return envFromStorage
    }

    return config.contentful.defaultEnvironment
  }

  private get usePreview() {
    if (config.contentful.showSelector) {
      return window.localStorage.getItem('teacher-ui-client.contentful.use-preview') === 'true'
    }
    return false
  }

  private get host() {
    return this.usePreview ? config.contentful.proxyPreviewHost : config.contentful.proxyHost
  }

  private get accessToken() {
    return CONTENTFUL_PROXY_ACCESS_TOKEN // client requires a non-empty value, but the proxy handles authentication
  }

  get clientConfig(): CreateClientParams {
    const { environment, accessToken, host } = this
    const headers = this.authToken.token ? { Authorization: `Bearer ${this.authToken.token}` } : undefined

    return {
      accessToken,
      environment,
      ...(headers ? { headers } : {}),
      host,
      space: config.contentful.spaceId,
    }
  }

  private refreshClientIfTokenChanged(): void {
    if (!this.clientToken) {
      this.clientToken = this.authToken.token
      return
    }

    if (this.clientToken !== this.authToken.token) {
      this.clientToken = this.authToken.token
      this.client = createClient(this.clientConfig).withoutUnresolvableLinks
    }
  }

  get contentfulLocale() {
    return getContentfulLocale(this.language)
  }

  get contentfulDomainLong() {
    return getContentfulLongDomainCode(this.countryCode)
  }

  get contentfulDomainShort() {
    return getContentfulShortDomainCode(this.countryCode)
  }

  private maybeHandleProxyUnauthorizedError(error: unknown): void {
    if (isUnauthorizedError(error)) {
      this.window.getWindow.location.assign(config.loginUrl)
    }
  }

  /**
   * A wrapper method around the client getEntries call, to ensure we can reliably report on contentful API errors.
   */
  async requestEntries<T extends EntrySkeletonType>(requestParams: any) {
    try {
      this.refreshClientIfTokenChanged()
      return await this.client.getEntries<T>(requestParams)
    } catch (error: unknown) {
      this.maybeHandleProxyUnauthorizedError(error)

      // only report for requests that weren't aborted (abort means likely user network issue)
      if (!isAbortError(error)) {
        this.log.error('Failed to getEntries from Contentful API', error ?? 'nullish', requestParams)
      }
      this.flashQueue.addFail({ subtitle: this.intl.t('contentful.getEntriesError') })
      return this.client.parseEntries<T>({ items: [], total: 0, skip: 0, limit: 0 })
    }
  }

  async requestEntry<T extends EntrySkeletonType>(id: string, query?: any) {
    try {
      this.refreshClientIfTokenChanged()
      return await this.client.getEntry<T>(id, query)
    } catch (error: unknown) {
      this.maybeHandleProxyUnauthorizedError(error)

      // only report for requests that weren't aborted (abort means likely user network issue)
      if (!isAbortError(error)) {
        this.log.error(`Failed to getEntry('${id}') from Contentful API`, error ?? 'nullish')
      }
      this.flashQueue.addFail({ subtitle: this.intl.t('contentful.getEntriesError') })
    }
  }

  /**
   * Book Sets can have 2 value categories or not have a category at all. Books with a category of "Reader" are seen on
   * the reader tab. Books with a general or undefined category are shown on the "Maths" or "Reading" tabs according to
   * their listed subscriptionType.
   */
  async getBookSets(printablesTab: PrintablesTabMeta) {
    const domain = this.contentfulDomainLong

    const requestParams: RequestParams = {
      content_type: 'printablesLibrarySet',
      locale: this.contentfulLocale,
      limit: 400,
    }

    // Determine request filters based on tab provided
    requestParams['fields.subscriptionType'] = printablesTab.subscriptionType
    if (printablesTab.category === PRINTABLES_CATEGORY_READER) {
      requestParams['fields.category'] = 'Reader'
    } else if (printablesTab.category === PRINTABLES_CATEGORY_GENERAL) {
      requestParams['fields.category[ne]'] = 'Reader'
    }

    requestParams['fields.domains[in]'] = ['All', domain]

    const entries = await this.requestEntries<PrintablesLibrarySetSkeleton>(requestParams)
    return entries.items
  }

  async getBookSetForBookCode(bookCode: string) {
    const domain = this.contentfulDomainLong

    const requestParams: RequestParams = {
      content_type: 'printablesLibrarySet',
      locale: this.contentfulLocale,
      limit: 1,
      'fields.programUrl': bookCode,
      'fields.domains[in]': ['All', domain],
    }

    const entries = await this.requestEntries<PrintablesLibrarySetSkeleton>(requestParams)
    return entries.items[0]
  }

  async getBook(bookCode: string) {
    const requestParams: RequestParams = {
      content_type: 'bookNotes', // repurposed from representing a book note to a book without changing the contentTypeId
      locale: this.contentfulLocale,
      limit: 1,
      'fields.bookCode': bookCode,
    }
    const entries = await this.requestEntries<BookNotesSkeleton>(requestParams)
    return entries.items[0]
  }

  async getBookNotesForBookCode(bookCode: string) {
    const bookEntry = await this.getBook(bookCode)
    return bookEntry?.fields.bookNotes?.map((bookNote) => ({ bookCode, ...bookNote })) || []
  }

  async getPrintablesQuickFilters() {
    const requestParams: RequestParams = { content_type: 'printablesQuickFilters', locale: this.contentfulLocale }
    const entries = await this.requestEntries<PrintablesQuickFiltersSkeleton>(requestParams)
    return entries.items
  }

  async getCurriculumMaps({
    subscriptionType,
    product,
  }: {
    subscriptionType: SubscriptionType
    product?: ProductUnderscored
  }) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'curriculumMap',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
      'fields.subscriptionType': capitalize(subscriptionType),
      'fields.product': product,
      'metadata.tags[exists]': false, // return tagless curriculum maps
    }

    const entries = await this.requestEntries<CurriculumMapSkeleton>(requestParams)
    return entries.items
  }

  /**
   * @param precincts
   * @param filter
   * @returns
   * @deprecated Use getPrecinctLessonsData
   */
  async getLessonPreviews(precincts: Precinct[], filter?: LessonPreviewFilter) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'lessonPreview',
      locale: this.contentfulLocale,
      'fields.lessonData.sys.contentType.sys.id': 'lessonData',
      'fields.lessonData.fields.precinct[in]': precincts,
      'fields.domains[in]': ['all', domain],
    }

    const phases = filter?.phases
    if (phases && !isEmpty(phases)) requestParams['fields.lessonData.fields.phase[in]'] = phases

    const { items } = await this.requestEntries<LessonPreviewSkeleton>(requestParams)
    return items
  }

  async getMathseedsPrimeLessons({
    pageNumber,
    perPage,
    searchQuery,
    threads,
    gradeIds,
  }: {
    pageNumber?: number
    perPage?: number
    searchQuery?: string
    threads?: MathseedsPrimeThread[]
    gradeIds?: GradeId[]
  }) {
    const requestParams: RequestParams = {
      locale: this.contentfulLocale,
      content_type: 'mathseedsPrimeLesson',
      'fields.curriculumId': 'au',
      order: ['fields.gradeId', 'fields.threadId', 'fields.unitId', 'fields.lessonId'],
      include: 2,
    }

    if (pageNumber && perPage) {
      requestParams.limit = perPage
      requestParams.skip = (pageNumber - 1) * perPage
    }
    if (isPresent(searchQuery)) {
      requestParams.query = searchQuery
    }

    if (!this.features.isEnabled('tui_ms_prime_measurement_thread')) {
      requestParams['fields.threadId[ne]'] = MathseedsPrimeThread.Measurement
    }

    if (threads && !isEmpty(threads)) requestParams['fields.threadId[in]'] = threads

    if (gradeIds && !isEmpty(gradeIds)) requestParams['fields.gradeId[in]'] = gradeIds

    return this.requestEntries<MathseedsPrimeLessonSkeleton>(requestParams)
  }

  async getPrecinctLessonsDataMapOrdered(
    precinct: Precinct,
    { pageNumber, perPage, searchQuery }: { pageNumber?: number; perPage?: number; searchQuery?: string },
  ) {
    const requestParams: RequestParams = {
      locale: this.contentfulLocale,
      content_type: 'lessonData',
      'fields.precinct': precinct,
      order: ['fields.map', 'fields.activityNumber'],
    }

    if (pageNumber && perPage) {
      requestParams.limit = perPage
      requestParams.skip = (pageNumber - 1) * perPage
    }
    if (isPresent(searchQuery)) {
      requestParams.query = searchQuery
    }
    return this.requestEntries<LessonDataSkeleton>(requestParams)
  }

  async getPrecinctLessonsData(
    precinct: Precinct,
    {
      pageNumber,
      perPage,
      searchQuery,
      filter,
    }: { pageNumber?: number; perPage?: number; searchQuery?: string; filter?: LessonPreviewFilter },
  ) {
    const requestParams: RequestParams = {
      locale: this.contentfulLocale,
      content_type: 'lessonData',
      'fields.precinct': precinct,
      order: ['fields.activityNumber'],
    }

    if (pageNumber && perPage) {
      requestParams.limit = perPage
      requestParams.skip = (pageNumber - 1) * perPage
    }
    if (isPresent(searchQuery)) {
      requestParams.query = searchQuery
    }

    const phases = filter?.phases
    if (phases && !isEmpty(phases)) requestParams['fields.phase[in]'] = phases

    return this.requestEntries<LessonDataSkeleton>(requestParams)
  }

  async getLessonsDataRelatedEntries({ items }: EntryCollection<LessonDataSkeleton>) {
    const lessonDataIds = items.map((item) => item.sys.id)
    const { resources, previews } = await hash({
      previews: this.getPreviewsForLessons(lessonDataIds),
      resources: this.getResourcesForLessons(lessonDataIds),
    })
    const previewByLessonDataId = keyBy(previews, 'fields.lessonData.sys.id')
    const resourcesByLessonDataId = groupBy(resources, 'fields.lessonData.sys.id')
    return { previewByLessonDataId, resourcesByLessonDataId }
  }

  async getLessonResources(precincts: Precinct[], resourceTypes: LessonResourceType[], filter: LessonResourceFilter) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'lessonResource',
      locale: this.contentfulLocale,
      'fields.resourceType[in]': resourceTypes,
      'fields.lessonData.sys.contentType.sys.id': 'lessonData',
      'fields.lessonData.fields.precinct[in]': precincts,
      'fields.domains[in]': ['all', domain],
    }

    const { phases } = filter ?? {}
    if (phases && !isEmpty(phases)) requestParams['fields.lessonData.fields.phase[in]'] = phases

    const { items } = await this.requestEntries<LessonResourceSkeleton>(requestParams)
    return items
  }

  /**
   * For a given list of LessonData entry ids, this function returns all LessonResources that link to them, grouped by
   * LessonData entry id.
   * {
   *   'id1': [resource1, resource2, ...],
   *   'id2': [resource3, resource4, ...],
   *   ...
   * }
   */
  async getLessonResourcesForLessonData(lessonDataEntryIds: string[]) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'lessonResource',
      locale: this.contentfulLocale,
      'fields.lessonData.sys.id[in]': lessonDataEntryIds,
      'fields.domains[in]': ['all', domain],
      limit: 200,
    }

    const { items } = await this.requestEntries<LessonResourceSkeleton>(requestParams)

    // group those resources by their lesson data id
    return groupBy(items, 'fields.lessonData.sys.id')
  }

  async getResourcesForLessons(lessonDataEntryIds: LessonDataId[]) {
    const matchCurriculum = {
      'fields.curriculum[in]': [this.accent],
    }
    const requestParams: RequestParams = {
      content_type: 'lessonResource',
      locale: this.contentfulLocale,
      ...matchCurriculum,
      'fields.lessonData.sys.id[in]': lessonDataEntryIds,
      'fields.domains[in]': ['all'],
    }
    const { items } = await this.requestEntries<LessonResourceSkeleton>(requestParams)
    return items
  }

  async getPreviewsForLessons(lessonDataEntryIds: LessonDataId[]) {
    const requestParams: RequestParams = {
      content_type: 'lessonPreview',
      'fields.accent': this.accent,
      locale: this.contentfulLocale,
      'fields.lessonData.sys.id[in]': lessonDataEntryIds,
      'fields.domains': ['all'],
    }
    const { items } = await this.requestEntries<LessonPreviewSkeleton>(requestParams)
    return items
  }

  async getPrecinctResources(precincts: Precinct[], resourceTypes: PrecinctResourceType[]) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'precinctResource',
      locale: this.contentfulLocale,
      'fields.resourceType[in]': resourceTypes,
      'fields.precinct[in]': precincts,
      'fields.domains[in]': ['all', domain],
    }

    const { items } = await this.requestEntries<PrecinctResourceSkeleton>(requestParams)
    return items
  }

  async getHomeschoolMaxWebinar({ slug }: { slug: string }) {
    const requestParams: RequestParams = {
      content_type: 'homeschoolMaxWebinar',
      locale: this.contentfulLocale,
      'fields.slug': slug,
      limit: 1,
    }

    const entries = await this.requestEntries<HomeschoolMaxWebinarSkeleton>(requestParams)
    return entries.items[0]
  }

  async getTeacherGuides({
    subscriptionType,
    product,
    role,
  }: {
    subscriptionType: SubscriptionType
    product?: ProductUnderscored
    role: Role
  }) {
    const domain = this.contentfulDomainShort

    const requestParams: RequestParams = {
      content_type: 'teacherGuide',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
      'fields.role': capitalize(role),
      'fields.subscriptionType': capitalize(subscriptionType),
      'metadata.tags[exists]': false, // return tagless teacher guides
    }
    if (product) {
      requestParams['fields.product'] = product
    }

    const entries = await this.requestEntries<TeacherGuideSkeleton>(requestParams)
    return entries.items
  }

  async getResearchReports({
    subscriptionType,
    product,
  }: {
    subscriptionType: SubscriptionType
    product?: ProductUnderscored
  }) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'researchReport',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
      'fields.subscriptionType': capitalize(subscriptionType),
      'metadata.tags[exists]': false, // return tagless research reports
    }
    if (product) {
      requestParams['fields.product'] = product
    }

    const entries = await this.requestEntries<ResearchReportSkeleton>(requestParams)
    return entries.items
  }

  async getCertificates({
    subscriptionType,
    product,
  }: {
    subscriptionType: SubscriptionType
    product: ProductUnderscored
  }) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'certificate',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
      'fields.subscriptionType': capitalize(subscriptionType),
      'fields.product': product,
    }
    const entries = await this.requestEntries<CertificateSkeleton>(requestParams)
    return entries.items
  }

  async getSummerGuides({ subscriptionType }: { subscriptionType: SubscriptionType }) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'summerGuide',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
      'fields.subscriptionType': capitalize(subscriptionType),
    }
    const entries = await this.requestEntries<SummerGuideSkeleton>(requestParams)
    return entries.items
  }

  async getCaseStudies({ subscriptionType }: { subscriptionType: SubscriptionType }) {
    const domain = this.contentfulDomainShort
    const requestParams: RequestParams = {
      content_type: 'caseStudy',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
      'fields.subscriptionType': capitalize(subscriptionType),
    }
    const entries = await this.requestEntries<CaseStudySkeleton>(requestParams)
    return entries.items
  }

  async getFastPhonicsEntries({ role }: { role: Role }) {
    const subscriptionType = SubscriptionType.Reading
    const product = 'fast_phonics'

    const researchReports = this.getResearchReports({ subscriptionType, product })
    const teacherGuides = this.getTeacherGuides({ subscriptionType, product, role })
    const curriculumMaps = this.getCurriculumMaps({ subscriptionType, product })
    const certificates = this.getCertificates({ subscriptionType, product })

    return hash({ researchReports, teacherGuides, curriculumMaps, certificates })
  }

  /**
   * Because printablesLibrarySet contains references to other content types,
   * it must be resolved using getEntries instead of getEntry.
   * see https://www.contentfulcommunity.com/t/getentry-only-returning-part-of-the-content/4804
   * @returns {Object} where key is a TUI user locale and value is a {PrintablesLibrarySet}
   */
  async getPreviewBookSet(entryId: string) {
    const printablesLibrarySetContentfulPreviewLocales = ['en-US', 'en-AU', 'en-GB']
    const entriesForLocales = await Promise.all(
      printablesLibrarySetContentfulPreviewLocales.map((locale) => {
        const requestParams: RequestParams = {
          content_type: 'printablesLibrarySet',
          locale,
          'sys.id': entryId,
        }
        return this.requestEntries<PrintablesLibrarySetSkeleton>(requestParams)
      }),
    )

    const [entryUS, entryAU, entryGB] = entriesForLocales.map((entry) => entry.items[0])

    return {
      us: entryUS,
      uk: entryGB,
      au: entryAU,
    }
  }

  async getRolloverGuide({ subscriptionType }: { subscriptionType: SubscriptionType }) {
    let entryId = ''
    switch (subscriptionType) {
      case SubscriptionType.Reading:
        entryId = '4P89trCKKHb53czGqEXJIk'
        break
      case SubscriptionType.Maths:
        entryId = '5TcPOQhjon2J5gTYF6bXw7'
        break
    }
    const requestParams: RequestParams = {
      content_type: 'rolloverGuide',
      locale: this.contentfulLocale,
    }

    const entry = await this.requestEntry<RolloverGuideSkeleton>(entryId, requestParams)

    if (!entry) this.log.error(`No entry found for rollover guide entry id: ${entryId}`)

    return entry
  }

  async getQuickStartResources({ subscriptionType }: { subscriptionType: SubscriptionType }) {
    const domain = this.contentfulDomainShort

    const requestParams: RequestParams = {
      content_type: 'quickStartResource',
      locale: this.contentfulLocale,
      'fields.domains[in]': ['all', domain],
    }
    // Filter by subscription type and allowed products
    if (subscriptionType === SubscriptionType.Reading) {
      requestParams['fields.product[in]'] = ['fp', 're', 'rex']
    }
    if (subscriptionType === SubscriptionType.Maths) {
      requestParams['fields.product[in]'] = ['ms']
    }
    if (subscriptionType === SubscriptionType.Writing) {
      requestParams['fields.product'] = 'wl'
    }

    const entries = await this.requestEntries<QuickStartResourceSkeleton>(requestParams)

    return entries.items
  }

  async getTeacherResourceCategories({
    subscriptionType,
  }: {
    subscriptionType: SubscriptionType.Reading | SubscriptionType.Maths
  }): Promise<TeacherResourceCategoryEntry | undefined> {
    const domain = this.contentfulDomainShort
    const includeDepth = 10

    const rootCategoryId = {
      [SubscriptionType.Reading]: 'reading-teacher-resource-category',
      [SubscriptionType.Maths]: 'maths-teacher-resource-category',
    }[subscriptionType]

    const entry = await this.requestEntry<TeacherResourceCategorySkeleton>(rootCategoryId, {
      content_type: 'teacherResourceCategory',
      locale: this.contentfulLocale,
      include: includeDepth,
    })

    if (!entry) return

    const byDomain = this.filterTeacherResourceCategoriesByDomain(domain, [entry])
    const rootEntry = byDomain[0] // root entry (reading or maths)
    if (rootEntry && isTypeTeacherResourceCategory(rootEntry)) {
      return this.rejectTeacherCategoriesMissingContent(rootEntry)
    }
    return
  }

  filterTeacherResourceCategoriesByDomain<E extends TeacherResourceCategoryEntry>(
    domain: 'in' | 'au' | 'ca' | 'cn' | 'uk' | 'kr' | 'nz' | 'th' | 'us' | 'za' | 'all',
    entries: (E | undefined)[],
  ): E[] {
    return entries.reduce<E[]>((entriesByDomain, entry) => {
      if (entry) {
        const newEntryFields: E['fields'] = { ...entry.fields }
        const { categories, resources } = entry.fields
        if (categories) newEntryFields.categories = this.filterTeacherResourceCategoriesByDomain(domain, categories)
        if (resources) newEntryFields.resources = this.filterTeacherResourcesByDomain(domain, resources)
        const newEntry: E = { ...entry, fields: newEntryFields }
        return [...entriesByDomain, newEntry]
      }
      return entriesByDomain
    }, [])
  }

  filterTeacherResourcesByDomain(
    domain: 'in' | 'au' | 'ca' | 'cn' | 'uk' | 'kr' | 'nz' | 'th' | 'us' | 'za' | 'all',
    entries: (
      | Entry<TeacherResourceFilesSkeleton | TeacherResourceActivitySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
      | undefined
    )[],
  ) {
    return entries.reduce<
      Entry<TeacherResourceFilesSkeleton | TeacherResourceActivitySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>[]
    >((entriesByDomain, entry) => {
      if ((entry && entry.fields.domains.includes('all')) || (entry && entry.fields.domains.includes(domain))) {
        return [...entriesByDomain, entry]
      }
      return entriesByDomain
    }, [])
  }

  /**
   * Given a teacher resource category without a sub-category or resource,
   * it is pruned from the teacher resources graph.
   */
  rejectTeacherCategoriesMissingContent(teacherResourceCategory: TeacherResourceCategoryEntry) {
    if (isEmpty(teacherResourceCategory.fields.categories) && isEmpty(teacherResourceCategory.fields.resources)) {
      return
    }

    if (Array.isArray(teacherResourceCategory.fields.categories)) {
      const newCategories = teacherResourceCategory.fields.categories.reduce<TeacherResourceCategoryEntry[]>(
        (acc, childCategory) => {
          if (childCategory && isTypeTeacherResourceCategory(childCategory)) {
            const remainingTeacherCategories = this.rejectTeacherCategoriesMissingContent(childCategory)
            if (remainingTeacherCategories) {
              return [...acc, remainingTeacherCategories]
            }
          }
          return acc
        },
        [],
      )
      teacherResourceCategory.fields.categories = newCategories
    }

    return teacherResourceCategory
  }
}

declare module '@ember/service' {
  interface Registry {
    'contentful-client': ContentfulClientService
  }
}
